if (!customElements.get('mobile-pinch-zoom')) {
  customElements.define(
    'mobile-pinch-zoom',
    class MobilePinchZoom extends HTMLElement {
      constructor() {
        super();
        // Pan speed relative to finger movement. Higher = faster pan.
        this._dragMultiplier = 1.5;
        // Current translate offset in px, applied via CSS custom properties.
        this._tx = 0;
        this._ty = 0;
        // Finger position at touchstart — used to compute tap origin and detect drag vs tap.
        this._startClientX = 0;
        this._startClientY = 0;
        // Previous finger position — used to compute incremental drag delta.
        this._lastClientX = 0;
        this._lastClientY = 0;
        // True once the finger has moved more than 5px; distinguishes drag from tap.
        this._hasMoved = false;

        this.addEventListener('touchstart', this._onTouchStart.bind(this), {
          passive: true,
        });
        // non-passive so preventDefault() can block page scroll while panning
        this.addEventListener('touchmove', this._onTouchMove.bind(this), {
          passive: false,
        });
        this.addEventListener('touchend', this._onTouchEnd.bind(this));
        // Capture-phase click block: prevents rapid taps from slipping a synthetic
        // click through to the modal-opener button before touchend can suppress it.
        this.addEventListener(
          'click',
          (e) => {
            if (this._isMobile()) {
              e.preventDefault();
              e.stopPropagation();
            }
          },
          true,
        );
      }

      _isMobile() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      // Writes _tx/_ty as px values onto the CSS custom properties consumed by the transform.
      _applyTranslate() {
        this.style.setProperty('--translateX', `${this._tx}px`);
        this.style.setProperty('--translateY', `${this._ty}px`);
      }

      _onTouchStart(e) {
        if (!this._isMobile() || e.touches.length !== 1) return;

        this._handleHighResImage();

        const touch = e.touches[0];
        this._startClientX = touch.clientX;
        this._startClientY = touch.clientY;
        this._lastClientX = touch.clientX;
        this._lastClientY = touch.clientY;
        this._hasMoved = false;
      }

      _handleHighResImage() {
        var highResImage = this.querySelector('.pinch-image');
        if (!highResImage || highResImage.src.length > 0) return;

        highResImage.addEventListener(
          'load',
          () => this.classList.add('highres-ready'),
          { once: true },
        );
        highResImage.src = highResImage.dataset.src;
      }

      _onTouchMove(e) {
        if (!this._isMobile() || e.touches.length !== 1) return;

        const touch = e.touches[0];

        // Always track movement so a slider swipe doesn't register as a tap.
        if (
          Math.abs(touch.clientX - this._startClientX) > 5 ||
          Math.abs(touch.clientY - this._startClientY) > 5
        ) {
          this._hasMoved = true;
        }

        if (!this.classList.contains('zoomed')) return;

        // Prevent page/slider scroll while the user is panning the zoomed image.
        e.preventDefault();

        const dx = touch.clientX - this._lastClientX;
        const dy = touch.clientY - this._lastClientY;

        // .dragging disables the CSS transition so pan feels instant.
        if (this._hasMoved) this.classList.add('dragging');

        const { width: W, height: H } = this.getBoundingClientRect();
        this._tx = Math.max(
          -W,
          Math.min(W, this._tx + dx * this._dragMultiplier),
        );
        this._ty = Math.max(
          -H,
          Math.min(H, this._ty + dy * this._dragMultiplier),
        );
        this._applyTranslate();

        this._lastClientX = touch.clientX;
        this._lastClientY = touch.clientY;
      }

      _onTouchEnd(e) {
        if (!this._isMobile()) return;
        // Suppress the synthetic click so the modal opener doesn't fire.
        e.preventDefault();
        this.classList.remove('dragging');

        // Drag ended — don't treat it as a tap.
        if (this._hasMoved) return;

        if (this.classList.contains('zoomed')) {
          // Tap while zoomed → zoom out.
          this.classList.remove('zoomed');
        } else {
          // Tap while normal → zoom in, centering on the tap point.
          // Formula: tx = W - 2*tapX
          // Derived from: 3*tapX + tx - W = tapX (tap point must stay fixed after scale(3)).
          const rect = this.getBoundingClientRect();
          const tapX = this._startClientX - rect.left;
          const tapY = this._startClientY - rect.top;

          // Taps within 15% of both a horizontal and vertical edge are considered
          // a corner — snap to centre so the zoomed image isn't clipped immediately.
          const cornerThreshold = 0.15;
          const edgeX = cornerThreshold * rect.width;
          const edgeY = cornerThreshold * rect.height;
          const inCorner =
            (tapX < edgeX || tapX > rect.width - edgeX) &&
            (tapY < edgeY || tapY > rect.height - edgeY);
          const zoomX = inCorner ? rect.width / 2 : tapX;
          const zoomY = inCorner ? rect.height / 2 : tapY;

          this._tx = rect.width - 2 * zoomX;
          this._ty = rect.height - 2 * zoomY;
          this._applyTranslate();
          this.classList.add('zoomed');
        }
      }
    },
  );
}
